import React from 'react'
import PropTypes from 'prop-types'
import stringify from 'json-stable-stringify'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import KitImage from './components/KitImage'
import Img from './components/Img'
import Icon from './components/Icon'
import DraggableItemIcons from './components/DraggableItemIcons'
import linkTypes from './enums/linkTypes'
import connectPropTypes from './utilities/connectPropTypes'
import getNormalisedParameters from './utilities/getNormalisedParameters'
import preventableBinder from './utilities/preventableBinder'
import errorTypes from './enums/errorTypes'
import messageTypes from './enums/messageTypes'
import AddButton from './components/AddButton'
import Collection from './components/Collection'
import displayModes from './enums/displayModes'
import Thing from './utilities/Data/Thing'
import ExpandableSection from './components/ExpandableSection'
import styles from './PostKit.icss'
import getDecodedURIComponent from './utilities/getDecodedURIComponent'
import urlForPostEdit from './utilities/urlForPostEdit'
import postKitConfig from './PostKitConfig'
import { firestore } from './utilities/firebase'

const maxItems = 15

class PostKit extends React.Component {
	constructor(props) {
		super(props)
		const parsedSearch = getNormalisedParameters(this.props.location.search)

		this.state = {
			isLoadingMoreKits: false,
			selectedKitIndex: undefined,
			kitId: getDecodedURIComponent(parsedSearch.kitid || props.match.params.kitId),
			postId: getDecodedURIComponent(parsedSearch.postid || props.match.params.kitId),
			doneButtonFunc: cb => cb()
		}

		this.handleAddItemClick = preventableBinder(this.handleAddItemClick.bind(this), 'itemForKit')
		this.handleTagsUpdate = this.handleTagsUpdate.bind(this)
		this.sortEndHandler = this.sortEndHandler.bind(this)
		this.setDoneButtonFunc = this.setDoneButtonFunc.bind(this)
		this.loadMoreKits = this.loadMoreKits.bind(this)
		this.selectExistingKit = this.selectExistingKit.bind(this)
		this.startFresh = this.startFresh.bind(this)
	}

	componentDidMount() {
		this.props.appHeading({ text: this.context.translate('Log/post your ride') })
		this.props.appBottomBarValue(linkTypes.ADD)
		this.props.appShowBottomBar(false)
		this.props.appShowBottomNavBar(true)
		this.props.appBottomNavBarHandleClick(() => {
			const kitDraft = { ...this.props.kitDraft }
			if (kitDraft.items.length < 2) {
				this.props.appMessage(messageTypes.ERROR, this.context.translate('You must add at least 2 items to a kit'), errorTypes.LOCALISEDERROR)
			} else {
				this.props.appNextButtonIsLoading(true)
				// save the kit if it has changed
				if (!this.props.kitDraftAsLoaded || this.props.kitDraftAsLoaded !== stringify(kitDraft)) {
					this.props.kitDraftSet({ kitDraft }, {
						success: ({ kitId }) => {
							this.state.doneButtonFunc(kitId, (imageUrl) => {
								const postId = this.state.postId
								if (imageUrl) {
									//	save the kit a 2nd time, so we can set the kit's imageUrl and kitId
									this.props.kitDraftSetImageUrlAndKitId({ kitId, imageUrl }, {
										success: () => {
											const ref = firestore.collection('kits').doc(kitId)
											let unsubscribeObserver
											const timeoutRedirect = setTimeout(() => {
												unsubscribeObserver()
												this.props.appNextButtonIsLoading(false)
											}, 10000)
											unsubscribeObserver = ref.onSnapshot((docSnap) => {
												if (docSnap.exists && docSnap.data().images && docSnap.data().images.length) {
													clearTimeout(timeoutRedirect)
													unsubscribeObserver()
													this.props.kitDraftSetImages({ images: docSnap.data().images })
													this.props.appNextButtonIsLoading(false)
													this.props.appRedirect(urlForPostEdit({ kitId, postId }))
												}
											}, (err) => {
												console.error(`[ERROR]: ${err}`)
											})
										},
										fail: () => {
											this.props.appNextButtonIsLoading(false)
										}
									})
								} else {
									this.props.appNextButtonIsLoading(false)
									this.props.appRedirect(urlForPostEdit({ kitId, postId }))
								}
							})
						},
						fail: () => {
							this.props.appNextButtonIsLoading(false)
						}
					})
				} else {
					const postId = this.state.postId
					this.props.appNextButtonIsLoading(false)
					this.props.appRedirect(urlForPostEdit({ kitId: kitDraft.kitId, postId }))
				}
			}
		})
		this.props.appBottomNavBarLabelNext(this.context.translate('NEXT'))
		if (this.state.kitId) {
			this.props.kitDraftFetch({ kitId: this.state.kitId })
		} else {
			let upKitsHasKitFromKitDraft = true
			if (this.props.kitDraft) {
				const userProfileKits = this.props.userProfileKits
				upKitsHasKitFromKitDraft = userProfileKits && userProfileKits.filter(upk => upk.kitId === this.props.kitDraft.kitId).length > 0
			}
			if (!upKitsHasKitFromKitDraft || (!this.props.kitDraft || Object.keys(this.props.kitDraft).length === 0)) {
				//	Grab existing kits for this user
				if (this.props.user && this.props.user.userProfile) {
					this.props.postDraftUserKitsFetch({ userProfile: this.props.user.userProfile, limit: postKitConfig.limit + 1 })
				}
			}
			this.props.kitDraftCreate()
		}
	}

	componentDidUpdate() {
		const kitDraft = this.props.kitDraft
		if (kitDraft) {
			if (kitDraft.uid !== this.props.user.uid && this.state.kitId) {
				//	User tried to edit someone else's kit - redirect to view.
				this.props.appRedirect(`/kit/${this.state.kitId}`)
			}
			const userProfileKits = this.props.userProfileKits
			if (this.state.selectedKitIndex === undefined && Object.keys(kitDraft).length > 0 && userProfileKits && userProfileKits.length > 0) {
				let foundKit = false
				userProfileKits.forEach((kit, index) => {
					if (kit.kitId === kitDraft.kitId) {
						foundKit = true
						this.setState({ selectedKitIndex: index })
					}
				})
				//	Use existing as long as we don't have a kit with items
				if (!foundKit && !(kitDraft.items && kitDraft.items.length > 0)) {
					this.selectExistingKit({}, 0)
				}
			}
		}
		if (kitDraft && !this.props.pageHasLoaded) {
			this.props.appPageLoaded(true)
		}
	}

	setDoneButtonFunc(doneButtonFunc) {
		this.setState({ doneButtonFunc })
	}

	loadMoreKits() {
		if (this.props.user && this.props.user.userProfile) {
			this.props.postDraftUserKitsFetch({ userProfile: this.props.user.userProfile })
			this.setState({ isLoadingMoreKits: true })
		}
	}

	selectExistingKit(kit, selectedKitIndex) {
		this.setState(() => ({ selectedKitIndex }))
		this.props.kitDraftSetFromKit({ kit: this.props.userProfileKits[selectedKitIndex] })
	}

	startFresh() {
		this.setState({ selectedKitIndex: null }, () => this.props.kitDraftClear({}))
	}

	handleAddItemClick() {
		this.props.appRedirect(`/postkit/search${this.state.kitId ? `/${this.state.kitId}` : ''}`)
	}

	handleTagsUpdate(tags) {
		this.props.kitDraftSetTags({ tags })
	}

	sortEndHandler({ oldIndex, newIndex }) {
		this.props.kitDraftItemsSort({ oldIndex, newIndex })
	}

	render() {
		const { kitDraft, userProfileKits } = this.props
		const numItems = (kitDraft && kitDraft.items && kitDraft.items.length) || 0
		const canAddItem = numItems < maxItems

		const renderIconsAfterItem = (renderProps) => {
			const { item, index } = renderProps
			return (
				<DraggableItemIcons
					item={item}
					index={index}
					handleMoveUpItem={index > 0 && numItems > 1 ? () => {
						this.sortEndHandler({ oldIndex: index, newIndex: index - 1 })
					} : undefined}
					handleMoveDownItem={index < (numItems - 2) && numItems > 1 ? () => {
						this.sortEndHandler({ oldIndex: index, newIndex: index + 1 })
					} : undefined}
					handleDeleteItem={() => {
						this.props.kitDraftItemDelete({ itemKey: item.itemKey, colourName: item.colourName })
					}}
				/>
			)
		}

		const renderTitleAfterItem = (renderProps) => {
			const { item, index } = renderProps
			return (
				<>
					{item.name && (
					<>
						<div className={styles.title}>
							<span className={styles.titleText}>{item.name}</span>
						</div>
						{this.state.selectedKitIndex === index &&
						<div className={styles.selectedBox} />
						}
					</>
					)}
				</>
			)
		}

		const kitReady = kitDraft && (!this.state.kitId || (this.state.kitId && kitDraft.kitId === this.state.kitId))

		return !userProfileKits || !kitReady ? null : (
			<section>
				{userProfileKits && userProfileKits.length > 0 && (
					<section className={styles.existingKits}>
						<Collection
							allowedDisplayModes={[displayModes.GRID.type]}
							classes={{ itemsContainer: styles.collectionRoot, itemRoot: styles.itemRoot }}
							items={userProfileKits.filter((kit, index) => (this.props.postKitUserKitsHasMoreKits ? index < postKitConfig.limit : true))}
							itemKey={Thing.key}
							itemTitle={Thing.title}
							itemImageContent={Thing.images}
							itemOnSelect={this.selectExistingKit}
							//	TODO: Add renderAfterItemIcons for the title and selected indicator.
							renderAfterItem={renderTitleAfterItem}
							renderAfterLastItem={(
								<div className={styles.startFreshButton}>
									<Button onClick={this.startFresh} classes={{ root: styles.startFreshItemHeight, label: styles.startFreshTextSurround }}>
										<div className={styles.startFreshText}>{this.context.translate('Start fresh +')}<Icon name="whiteKit" /></div>
									</Button>
								</div>
							)}
						/>
						{this.props.postKitUserKitsHasMoreKits && (
						<>
							{this.state.isLoadingMoreKits ?
								<CircularProgress size={20} thickness={4} className={styles.loadMoreSpinner} /> :
								<AddButton onClick={this.loadMoreKits} className={styles.moreButton}>{this.context.translate('More')}</AddButton>
							}
						</>
						)}
					</section>
				)}
				{kitReady && (
					<div className={styles.kitCreationSurround}>
						<section className={styles.leadImage}>
							<KitImage
								kitItems={kitDraft.items}
								kitTags={kitDraft.tags}
								kitTemplateSettings={kitDraft.templateSettings}
								handleTemplateUpdate={(templateSettings) => {
									this.props.kitDraftTemplateUpdate({ templateSettings })
								}}
								handleTagsUpdate={this.handleTagsUpdate}
								kitId={this.state.kitId}
								onCompleteBinder={this.setDoneButtonFunc}
								hintMessage={(
									<button type="button" onClick={this.handleAddItemClick} className={styles.addItemButton}>
										<Img content="/images/itemPlaceHolders/kit.jpg" className={styles.placeholderImage} />
										<div className={styles.placeholderTextSurround}>
											<div className={styles.placeholderText}>
												<div>{this.context.translate('Add Items of Kit to your Post')}</div>
												<div className={styles.placeholderSubtext}>{this.context.translate('Create a list of items you wear from the things you own or from cool items you like')}</div>
											</div>
										</div>
									</button>
								)}
							/>
						</section>

						<section>
							<ExpandableSection title={this.context.translate('{numItems|1 item|# items}', { numItems })} defaultExpanded>
								<Collection
									classes={{ itemRoot: styles.itemRoot }}
									items={kitDraft.items}
									itemKey={kitItem => Thing.key({ ...kitItem.item, colourName: kitItem.colourName })}
									itemTitle={kitItem => Thing.title(kitItem.item)}
									itemSubtitle={kitItem => Thing.subtitle(kitItem.item)}
									itemImageContent={kitItem => kitItem.itemImages}
									itemHref={kitItem => Thing.href(kitItem.item)}
									itemOnSelect={kitItem => this.props.appRedirect(Thing.href({ ...kitItem.item, colour: { colourName: kitItem.colourName } }))}
									sortable
									sortableProps={{
										onSortEnd: this.sortEndHandler
									}}
									renderAfterItem={renderIconsAfterItem}
								/>
							</ExpandableSection>
							<div className={styles.addButtonSurround}>
								{canAddItem &&
									<AddButton onClick={this.handleAddItemClick} className={styles.addButton}>{this.context.translate('Add items')}</AddButton>
								}
								{!canAddItem &&
									<div className={styles.maxTagsReached}>{this.context.translate('You have added the maximum number of items ({maxItems})', { maxItems })}</div>
								}
							</div>
						</section>
					</div>
				)}
			</section>
		)
	}
}

PostKit.contextTypes = {
	translate: PropTypes.func
}

PostKit.propTypes = {
	location: PropTypes.shape({
		pathname: PropTypes.string.isRequired,
		search: PropTypes.string
	}).isRequired,
	match: PropTypes.shape({
		params: PropTypes.object.isRequired
	}).isRequired,
	appHeading: PropTypes.func.isRequired,
	appBottomBarValue: PropTypes.func.isRequired,
	appRedirect: PropTypes.func.isRequired,
	appMessage: PropTypes.func.isRequired,
	appShowBottomBar: PropTypes.func.isRequired,
	appShowBottomNavBar: PropTypes.func.isRequired,
	appBottomNavBarHandleClick: PropTypes.func.isRequired,
	appBottomNavBarLabelNext: PropTypes.func.isRequired,
	appNextButtonIsLoading: PropTypes.func.isRequired,
	appPageLoaded: PropTypes.func.isRequired,
	pageHasLoaded: PropTypes.bool.isRequired,
	kitDraft: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	kitDraftAsLoaded: PropTypes.string,
	userProfileKits: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	postKitUserKitsHasMoreKits: PropTypes.bool,
	kitDraftFetch: PropTypes.func.isRequired,
	kitDraftItemDelete: PropTypes.func.isRequired,
	kitDraftSet: PropTypes.func.isRequired,
	kitDraftSetImages: PropTypes.func.isRequired,
	kitDraftSetFromKit: PropTypes.func.isRequired,
	kitDraftSetImageUrlAndKitId: PropTypes.func.isRequired,
	kitDraftSetTags: PropTypes.func.isRequired,
	kitDraftTemplateUpdate: PropTypes.func.isRequired,
	kitDraftItemsSort: PropTypes.func.isRequired,
	kitDraftCreate: PropTypes.func.isRequired,
	kitDraftClear: PropTypes.func.isRequired,
	user: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
	postDraftUserKitsFetch: PropTypes.func.isRequired
}

PostKit.defaultProps = {
	kitDraft: undefined,
	kitDraftAsLoaded: undefined,
	userProfileKits: undefined,
	postKitUserKitsHasMoreKits: true
}

export const testing = {
	PostKit
}

export default connectPropTypes(PostKit)



// WEBPACK FOOTER //
// ./src/PostKit.jsx